import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";

const emptyForm = { name: "", email: "" };

export default function AdminUsers() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | { mode: "create" } | { mode: "edit", id, original }
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // user row or null

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch("/adminuser");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || "Failed to load admin users.");
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setEditing({ mode: "create" });
  };

  const openEdit = (row) => {
    setForm({ name: row.name, email: row.email });
    setFormError(null);
    setEditing({ mode: "edit", id: row.id });
  };

  const closeDialog = () => {
    if (saving) return;
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setFormError("Name and email are required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = editing.mode === "edit";
      const url = isEdit ? `/adminuser/${editing.id}` : "/adminuser";
      const method = isEdit ? "PUT" : "POST";
      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim() })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/adminuser/${pendingDelete.id}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      setPendingDelete(null);
      await load();
    } catch (err) {
      setError(err.message || "Failed to delete.");
      setPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          Admin Users
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Admin User
        </Button>
      </Stack>

      {error && (
        <Box
          sx={{ p: 2, mb: 2, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
        >
          {error}
        </Box>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No admin users yet.</Box>
      )}

      {rows && rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead
              sx={{ "& .MuiTableCell-head": { bgcolor: colors.darkGray, color: "white", fontWeight: 700 } }}
            >
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>
                    {r.updatedAt ? (
                      <Tooltip
                        arrow
                        title={
                          r.createdAt
                            ? `Created by ${r.createdByName || r.createdBy || "—"} on ${fmtDateTime(r.createdAt)}`
                            : "No creation record"
                        }
                      >
                        <span>
                          {fmtDateTime(r.updatedAt)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {r.updatedByName || r.updatedBy || "—"}
                          </Typography>
                        </span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setPendingDelete(r)}
                      aria-label="Delete"
                      sx={{ color: colors.error }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editing !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.mode === "edit" ? "Edit Admin User" : "New Admin User"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              fullWidth
              placeholder="[EmployeeID]@sbcountyatc.gov"
              helperText="Format: [EmployeeID]@sbcountyatc.gov"
            />
            {formError && (
              <Box
                sx={{ p: 1.5, bgcolor: colors.errorBg, color: colors.error, borderRadius: 1, fontSize: 13 }}
              >
                {formError}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingDelete !== null} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>Delete admin user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{pendingDelete?.name}</strong> ({pendingDelete?.email}) from the admin user list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
